import { useState, type KeyboardEvent } from 'react'

interface MultiSuggestInputProps {
  values: string[]
  onChange: (values: string[]) => void
  suggestions?: string[]
  placeholder?: string
  suggestionsLabel?: string
}

/**
 * Chip-based multi-select — type and press Enter to add a value, or click
 * a suggestion chip (sourced from real profile data, e.g.
 * CandidateFilterOptionsController) to add it directly. Used for
 * Candidate Search v2's keyword/skills/location filters, which all need
 * "select several values with suggestions" the same way.
 */
export function MultiSuggestInput({
  values,
  onChange,
  suggestions = [],
  placeholder = 'Type and press Enter…',
  suggestionsLabel = 'Suggestions',
}: MultiSuggestInputProps) {
  const [draft, setDraft] = useState('')

  const addValue = (value: string) => {
    const trimmed = value.trim()
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed])
    }
    setDraft('')
  }

  const removeValue = (value: string) => {
    onChange(values.filter((v) => v !== value))
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addValue(draft)
    } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      const last = values[values.length - 1]
      if (last) removeValue(last)
    }
  }

  const visibleSuggestions = suggestions.filter((s) => !values.includes(s)).slice(0, 10)

  return (
    <div>
      <div className="form-control d-flex flex-wrap align-items-center gap-1" style={{ minHeight: 44 }}>
        {values.map((value) => (
          <span
            key={value}
            className="badge bg-primary-transparent d-flex align-items-center gap-1 py-1 px-2"
          >
            {value}
            <button
              type="button"
              className="btn-close fs-10"
              aria-label={`Remove ${value}`}
              onClick={() => removeValue(value)}
            />
          </span>
        ))}
        <input
          type="text"
          className="border-0 flex-grow-1"
          style={{ outline: 'none', minWidth: 120 }}
          placeholder={values.length === 0 ? placeholder : 'Type another…'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => addValue(draft)}
        />
      </div>
      {visibleSuggestions.length > 0 && (
        <div className="mt-2">
          <p className="fs-11 text-muted mb-1">{suggestionsLabel}</p>
          <div className="d-flex flex-wrap gap-1">
            {visibleSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                className="btn btn-sm btn-light border rounded-pill fs-12 py-1 px-2"
                onClick={() => addValue(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
